import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";

/* ── Shared style tokens ───────────────────────────────────── */
const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const errorStyle = { fontSize: 11, color: "#C62828", marginTop: 4 };
const readOnlyStyle = { ...inputStyle, background: "#F7FAFC", color: "#718096", cursor: "not-allowed" };

const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    {children}
    {error && <div style={errorStyle}>{error}</div>}
  </div>
);

const Input = ({ error, readOnly, ...props }) => (
  <input
    style={readOnly ? readOnlyStyle : { ...inputStyle, borderColor: error ? "#E53E3E" : "#E2E8F0" }}
    onFocus={(e) => { if (!readOnly) e.target.style.borderColor = "#4CAF50"; }}
    onBlur={(e) => { if (!readOnly) e.target.style.borderColor = error ? "#E53E3E" : "#E2E8F0"; }}
    readOnly={readOnly}
    {...props}
  />
);

const Select = ({ error, children, ...props }) => (
  <select
    style={{ ...inputStyle, borderColor: error ? "#E53E3E" : "#E2E8F0", cursor: "pointer" }}
    onFocus={(e) => e.target.style.borderColor = "#4CAF50"}
    onBlur={(e) => e.target.style.borderColor = error ? "#E53E3E" : "#E2E8F0"}
    {...props}
  >
    {children}
  </select>
);

const Section = ({ icon, title, children, cols = 2 }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #EDF2F7" }}>
      <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, flexShrink: 0 }}>
        <i className={`mdi ${icon}`} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#2D3748" }}>{title}</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: "0 20px" }}>
      {children}
    </div>
  </div>
);

/* ── EditVFC ─────────────────────────────────────────────────── */
const EditVFC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const Api = process.env.REACT_APP_API;

  const [formData, setFormData] = useState({
    vfcName: "", rangeId: "", district: "", beatName: "", lgdCode: "",
    beatCode: "", bdoOfficeName: "", bdoOfficeCode: "", location: "",
    address: "", villageName: "", villageCode: "", panchayath: "",
    thaluk: "", thalukCode: "", pinCode: "", dateOfEstablishment: "",
    bankName: "", branch: "", accountNumber: "", ifscCode: "",
    registrationNo: "", registrationDate: "", totalFamily: "", totalPopulation: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [rangeOptions, setRangeOptions] = useState([]);
  const [beatOptions, setBeatOptions] = useState([]);
  const [talukOptions, setTalukOptions] = useState([]);

  const validateForm = (d) => {
    const e = {};

    if (!d.vfcName) e.vfcName = "JFMC name is required";
    if (!d.rangeId) e.rangeId = "Please select Range";
    if (!d.location) e.location = "Please enter Location";
    if (!d.address) e.address = "Please enter Address";

    // FIXED
    if (!d.villageName) e.villageName = "Please enter Village";

    if (!d.panchayath) e.panchayath = "Please enter Panchayath";
    if (!d.thaluk) e.thaluk = "Please enter Thaluk";
    if (!d.pinCode) e.pinCode = "Please enter Pincode";

    if (!d.dateOfEstablishment) {
      e.dateOfEstablishment = "Please enter Date of Establishment";
    }

    return e;
  };

  useEffect(() => {
    setLoading(true);
    const fetchDistricts = async () => {
      try {
        const Config = Authorization();
        const res = await axios.get(`${Api}/api/lgd/districts`, Config);
        setDistricts(res.data);
      } catch (err) { console.error("Failed to fetch districts", err); }
    };
    const fetchVfc = async () => {
      try {
        const Config = Authorization();

        const res = await axios.get(`${Api}/api/vfcs/${id}`, Config);

        const vfcData = res.data.vfc;

        let formattedDate = "";

        if (vfcData.dateOfEstablishment) {
          formattedDate = vfcData.dateOfEstablishment.split("T")[0];
        }

        setFormData({
          ...vfcData,

          villageName: vfcData.villageName || vfcData.village || "",
          address: vfcData.address || "",
          panchayath: vfcData.panchayath || "",
          thaluk: vfcData.thaluk || "",
          pinCode: vfcData.pinCode || "",

          bankName: vfcData.bankName || "",
          branch: vfcData.branch || "",
          accountNumber: vfcData.accountNumber || "",
          ifscCode: vfcData.ifscCode || "",

          registrationNo: vfcData.registrationNo || "",
          registrationDate: vfcData.registrationDate
            ? vfcData.registrationDate.split("T")[0]
            : "",

          totalFamily: vfcData.totalFamily || "",
          totalPopulation: vfcData.totalPopulation || "",

          dateOfEstablishment: formattedDate || "",
        });

      } catch (err) {
        console.error("Error fetching JFMC data", err);
      } finally {
        setLoading(false);
      }
    };
    const fetchRanges = async () => {
      try {
        const Config = Authorization();
        const res = await axios.get(`${Api}/api/ranges`, Config);
        if (Array.isArray(res.data)) setRangeOptions(res.data);
      } catch (err) { console.error("Error fetching ranges", err); }
    };
    if (id) fetchVfc();
    fetchDistricts();
    fetchRanges();
  }, [id, Api]);

  const fetchBeatsForRange = async (rangeId) => {
    try {
      const Config = Authorization();
      const res = await axios.get(`${Api}/api/beat/by-range/${rangeId}`, Config);
      setBeatOptions(res.data.beats);
    } catch (err) { console.error("Error fetching beats", err); }
  };

  const fetchBlocks = async (districtCode) => {
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/lgd/blocks?districtCode=${districtCode}`, config);
      setBlocks(res.data.blocks || []);
      setTalukOptions(res.data.taluks || []);
    } catch (err) { console.error(err); }
  };

  const fetchVillages = async (blockCode) => {
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/lgd/villages?districtCode=${formData.district}&blockCode=${blockCode}`, config);
      setVillages(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPinCode = async (villageName) => {
    try {
      if (!villageName) return;
      const cleanedName = villageName.trim().replace(/\s+/g, " ");
      const response = await axios.get(`https://api.postalpincode.in/postoffice/${encodeURIComponent(cleanedName)}`);
      const postData = response.data?.[0];
      if (postData?.Status === "Success" && postData?.PostOffice?.length > 0) {
        setFormData((prev) => ({ ...prev, pinCode: postData.PostOffice[0].Pincode }));
      } else {
        setFormData((prev) => ({ ...prev, pinCode: "" }));
      }
    } catch (err) { console.error("Error fetching pincode", err); }
  };

  const handleRangeChange = async (e) => {
    const selectedRangeId = e.target.value;
    setFormData((prev) => ({ ...prev, rangeId: selectedRangeId, beatName: "", beatCode: "" }));
    if (selectedRangeId) await fetchBeatsForRange(selectedRangeId);
  };

  const handleBeatNameChange = (e) => {
    const selectedBeatName = e.target.value;
    const beat = beatOptions.find((b) => b.beatName === selectedBeatName);
    setFormData((prev) => ({ ...prev, beatName: selectedBeatName, beatCode: beat ? beat.beatCode : "" }));
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    const updated = { ...formData, [id]: value };

    if (id === "district") {
      const sel = districts.find((d) => d.districtCode.toString() === value);
      if (sel) { updated.district = sel.districtCode; updated.lgdCode = sel.districtCode; fetchBlocks(Number(value)); }
    } else if (id === "bdoOfficeName") {
      const sel = blocks.find((b) => b.blockName === value);
      if (sel) { updated.bdoOfficeName = sel.blockName; updated.bdoOfficeCode = sel.blockCode; fetchVillages(sel.blockCode); }
    } else if (id === "villageName") {
      const sel = villages.find((v) => v.villageName === value);
      if (sel) {
        updated.villageName = sel.villageName; updated.villageCode = sel.villageCode;
        if (sel.talukName && sel.talukCode) { updated.thaluk = sel.talukName; updated.thalukCode = sel.talukCode; }
        else { updated.thaluk = ""; updated.thalukCode = ""; }
        fetchPinCode(sel.villageName);
      }
    } else if (id === "thaluk") {
      const sel = talukOptions.find((t) => t.talukName === value);
      if (sel) { updated.thaluk = sel.talukName; updated.thalukCode = sel.talukCode; }
      else { updated.thalukCode = ""; }
    }
    setFormData(updated);
    if (errors[id]) setErrors((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const Config = Authorization();
      const response = await axios.put(`${Api}/api/vfcs/${id}`, formData, Config);
      if (response.status === 200) {
        toast.success("Updated Successfully");
        navigate("/vfc/manage");
      } else {
        toast.error("Error updating form");
      }
    } catch (error) {
      console.error("Error updating form:", error);
      toast.error("Error updating form");
    } finally {
      setSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Edit JFMC</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>JFMC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Edit JFMC</span>
        </div>
      </div>

      {/* ── Form Card ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>

        {/* Card header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
            <i className="mdi mdi-home-edit-outline" />
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>JFMC Edit Form</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Fields marked * are required</div>
          </div>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} style={{ padding: "24px" }}>

          <Section icon="mdi-home-group" title="Basic Information">
            <Field label={<>JFMC Name <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.vfcName}>
              <Input id="vfcName" placeholder="Enter JFMC Name" value={formData.vfcName} onChange={handleChange} error={errors.vfcName} />
            </Field>
            <Field label={<>Range <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.rangeId}>
              <Select id="rangeId" value={formData.rangeId} onChange={handleRangeChange} error={errors.rangeId}>
                <option value="">Select Range</option>
                {rangeOptions.sort((a, b) => a.rangeName.localeCompare(b.rangeName)).map((r) => (
                  <option key={r._id} value={r._id}>{r.rangeName}</option>
                ))}
              </Select>
            </Field>
            <Field label="Beat Name">
              <Select id="beatName" value={formData.beatName} onChange={handleBeatNameChange}>
                <option value="">Select Beat</option>
                {beatOptions.sort((a, b) => a.beatName.localeCompare(b.beatName)).map((b) => (
                  <option key={b._id} value={b.beatName}>{b.beatName}</option>
                ))}
              </Select>
            </Field>
            <Field label="Beat Code">
              <Input id="beatCode" value={formData.beatCode} onChange={handleChange} readOnly />
            </Field>
          </Section>

          <Section icon="mdi-map-marker-outline" title="Location Details">
            <Field label={<>District <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.district}>
              <Select id="district" value={formData.district} onChange={handleChange} error={errors.district}>
                <option value="">Select District</option>
                {districts.sort((a, b) => a.districtName.localeCompare(b.districtName)).map((d) => (
                  <option key={d._id} value={d.districtCode}>{d.districtName}</option>
                ))}
              </Select>
            </Field>
            <Field label="LGD Code">
              <Input id="lgdCode" value={formData.lgdCode} onChange={handleChange} readOnly />
            </Field>
            <Field label="BDO Office Name">
              <Select id="bdoOfficeName" value={formData.bdoOfficeName} onChange={handleChange}>
                <option value="">Select Block</option>
                {blocks.sort((a, b) => a.blockName.localeCompare(b.blockName)).map((b) => (
                  <option key={b.blockCode} value={b.blockName}>{b.blockName}</option>
                ))}
              </Select>
            </Field>
            <Field label="BDO Office Code">
              <Input id="bdoOfficeCode" value={formData.bdoOfficeCode} onChange={handleChange} readOnly />
            </Field>
            <Field label="Village Name" error={errors.villageName}>
              <Select id="villageName" value={formData.villageName} onChange={handleChange} error={errors.villageName}>
                <option value="">Select Village</option>
                {villages.sort((a, b) => a.villageName.localeCompare(b.villageName)).map((v) => (
                  <option key={v._id} value={v.villageName}>{v.villageName}</option>
                ))}
              </Select>
            </Field>
            <Field label="Village Code">
              <Input id="villageCode" value={formData.villageCode} onChange={handleChange} readOnly />
            </Field>
            <Field label={<>Location <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.location}>
              <Input id="location" placeholder="Enter Location" value={formData.location} onChange={handleChange} error={errors.location} />
            </Field>
            <Field label={<>Address <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.address}>
              <Input id="address" placeholder="Enter Address" value={formData.address} onChange={handleChange} error={errors.address} />
            </Field>
            <Field label={<>Panchayath <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.panchayath}>
              <Input id="panchayath" placeholder="Enter Panchayath" value={formData.panchayath} onChange={handleChange} error={errors.panchayath} />
            </Field>
            <Field label={<>Pin Code <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.pinCode}>
              <Input id="pinCode" type="number" placeholder="Auto-loaded" value={formData.pinCode} onChange={handleChange} error={errors.pinCode} />
            </Field>
            <Field label={<>Thaluk <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.thaluk}>
              <Select id="thaluk" value={formData.thaluk} onChange={handleChange} error={errors.thaluk}>
                <option value="">Select Thaluk</option>
                {talukOptions.map((t) => (
                  <option key={t.talukName} value={t.talukName}>{t.talukName}</option>
                ))}
              </Select>
            </Field>
            <Field label="Thaluk Code">
              <Input id="thalukCode" value={formData.thalukCode} onChange={handleChange} readOnly />
            </Field>
          </Section>

          <Section icon="mdi-file-certificate-outline" title="Registration Details">
            <Field label={<>Date of Establishment <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.dateOfEstablishment}>
              <Input id="dateOfEstablishment" type="date" value={formData.dateOfEstablishment} onChange={handleChange} error={errors.dateOfEstablishment} />
            </Field>
            <Field label="Registration Number">
              <Input id="registrationNo" placeholder="Enter Registration Number" value={formData.registrationNo} onChange={handleChange} />
            </Field>
            <Field label="Registration Date">
              <Input id="registrationDate" type="date" value={formData.registrationDate} onChange={handleChange} />
            </Field>
            <div />
            <Field label="Total Family">
              <Input id="totalFamily" placeholder="Enter Total Family" value={formData.totalFamily} onChange={handleChange} />
            </Field>
            <Field label="Total Population">
              <Input id="totalPopulation" placeholder="Enter Total Population" value={formData.totalPopulation} onChange={handleChange} />
            </Field>
          </Section>

          <Section icon="mdi-bank-outline" title="Bank Details">
            <Field label="Bank Name" error={errors.bankName}>
              <Input id="bankName" placeholder="Enter Bank Name" value={formData.bankName} onChange={handleChange} error={errors.bankName} />
            </Field>
            <Field label="Branch" error={errors.branch}>
              <Input id="branch" placeholder="Enter Branch" value={formData.branch} onChange={handleChange} error={errors.branch} />
            </Field>
            <Field label="Account Number" error={errors.accountNumber}>
              <Input id="accountNumber" placeholder="Enter Account Number" value={formData.accountNumber} onChange={handleChange} error={errors.accountNumber} />
            </Field>
            <Field label="IFSC Code" error={errors.ifscCode}>
              <Input id="ifscCode" placeholder="Enter IFSC Code" value={formData.ifscCode} onChange={handleChange} error={errors.ifscCode} />
            </Field>
          </Section>

          {/* ── Actions ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 8, borderTop: "1px solid #EDF2F7", marginTop: 8 }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 28px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
                color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
                cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1,
                boxShadow: "0 3px 10px rgba(46,125,50,0.3)",
              }}
            >
              <i className={`mdi ${submitting ? "mdi-loading mdi-spin" : "mdi-content-save-outline"}`} />
              {submitting ? "Saving…" : "Update JFMC"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/vfc/manage")}
              style={{
                padding: "10px 20px", background: "#fff", color: "#4A5568",
                border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: 13,
                fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditVFC;
